import { useState } from 'react'
import { MentorSidebar } from '@/components/mentor/mentor-sidebar'
import { MentorHeader } from '@/components/mentor/mentor-header'
import { MentorBottomNav } from '@/components/mentor/mentor-bottom-nav'
import { MentorMobileMenu } from '@/components/mentor/mentor-mobile-menu'
import { AssignedStudentsView } from '@/components/mentor/assigned-students-view'
import { DailyPlanBuilderView } from '@/components/mentor/daily-plan-builder-view'
import { ApprovalQueueView } from '@/components/mentor/approval-queue-view'
import { StudentDetailView } from '@/components/mentor/student-detail-view'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { TestManagementView } from '@/components/mentor/test-management-view'
import { QuestionBankManagementView } from '@/components/mentor/question-bank-management-view'
import { MentorAnalyticsView } from '@/components/mentor/mentor-analytics-view'
import { MentorChatView } from '@/components/mentor/mentor-chat-view'
import { MentorAnnouncementsView } from '@/components/mentor/mentor-announcements-view'
import { MentorProfileView } from '@/components/mentor/mentor-profile-view'
import { StudyMaterialsView } from '@/components/mentor/study-materials-view'
import {
    Users, Clock, TrendingUp, AlertTriangle,
    Calendar, BarChart3, MessageSquare
} from 'lucide-react'
import { mockMentorStudents, mockPendingReports, getStudentsNeedingAttention } from '@/data/mentor-mock-data'

export function MentorDashboardPage() {
    const [activeTab, setActiveTab] = useState('dashboard')
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null)

    const needsAttention = getStudentsNeedingAttention()
    const pendingApprovals = mockPendingReports.filter((r: any) => r.approvalStatus === 'pending').length
    const totalStudents = mockMentorStudents.length
    const avgDisciplineScore = (mockMentorStudents.reduce((sum: number, s: any) => sum + s.disciplineScore, 0) / totalStudents).toFixed(1)

    const handleMobileMenuNavigate = (tab: string) => {
        setActiveTab(tab)
        setIsMobileMenuOpen(false)
    }

    const handleViewStudent = (studentId: string) => {
        setSelectedStudentId(studentId)
        setActiveTab('student-detail')
    }

    const handleBackFromStudent = () => {
        setSelectedStudentId(null)
        setActiveTab('students')
    }

    const renderContent = () => {
        switch (activeTab) {
            case 'students':
                return <AssignedStudentsView onViewStudent={handleViewStudent} />
            case 'student-detail':
                return selectedStudentId ? (
                    <StudentDetailView studentId={selectedStudentId} onBack={handleBackFromStudent} />
                ) : null
            case 'plan-builder':
                return <DailyPlanBuilderView />
            case 'attendance':
                return <ApprovalQueueView />
            case 'tests':
                return <TestManagementView />
            case 'question-bank':
                return <QuestionBankManagementView />
            case 'analytics':
                return <MentorAnalyticsView />
            case 'chat':
                return <MentorChatView />
            case 'announcements':
                return <MentorAnnouncementsView />
            case 'profile':
                return <MentorProfileView />
            case 'materials':
                return <StudyMaterialsView />
            default:
                return <DashboardOverview />
        }
    }

    function DashboardOverview() {
        return (
            <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
                {/* Welcome Header */}
                <div>
                    <h2 className="text-xl md:text-2xl font-bold text-theme-primary">
                        Welcome back, Dr. Sarah 👋
                    </h2>
                    <p className="text-sm text-theme-secondary mt-1">
                        Here's what's happening with your students today
                    </p>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <GlassmorphicCard className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-theme-secondary">Total Students</p>
                                <p className="text-2xl font-bold text-theme-primary mt-1">{totalStudents}</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <Users className="h-6 w-6 text-primary-blue" />
                            </div>
                        </div>
                    </GlassmorphicCard>

                    <GlassmorphicCard className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-theme-secondary">Pending Approvals</p>
                                <p className="text-2xl font-bold text-theme-primary mt-1">{pendingApprovals}</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                                <Clock className="h-4 w-4 text-amber-600" />
                            </div>
                        </div>
                    </GlassmorphicCard>

                    <GlassmorphicCard className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-theme-secondary">Needs Attention</p>
                                <p className="text-2xl font-bold text-theme-primary mt-1">{needsAttention.length}</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                <AlertTriangle className="h-6 w-6 text-red-600" />
                            </div>
                        </div>
                    </GlassmorphicCard>

                    <GlassmorphicCard className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-theme-secondary">Avg Discipline</p>
                                <p className="text-2xl font-bold text-theme-primary mt-1">{avgDisciplineScore}/10</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                <TrendingUp className="h-6 w-6 text-green-600" />
                            </div>
                        </div>
                    </GlassmorphicCard>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <GlassmorphicCard
                        className="p-6 cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-amber-500"
                        onClick={() => setActiveTab('attendance')}
                    >
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
                                <Clock className="h-6 w-6 text-amber-600" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-bold text-theme-primary mb-1">Approve Hours</h3>
                                <p className="text-sm text-theme-secondary mb-3">
                                    {pendingApprovals} reports waiting for review
                                </p>
                                <button className="text-sm text-primary-blue font-semibold hover:underline">
                                    Review Now →
                                </button>
                            </div>
                        </div>
                    </GlassmorphicCard>

                    <GlassmorphicCard
                        className="p-6 cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-blue-500"
                        onClick={() => setActiveTab('plan-builder')}
                    >
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                                <Calendar className="h-6 w-6 text-primary-blue" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-bold text-theme-primary mb-1">Build Daily Plan</h3>
                                <p className="text-sm text-theme-secondary mb-3">
                                    Assign tasks for today or the week
                                </p>
                                <button className="text-sm text-primary-blue font-semibold hover:underline">
                                    Create Plan →
                                </button>
                            </div>
                        </div>
                    </GlassmorphicCard>

                    <GlassmorphicCard
                        className="p-6 cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-red-500"
                        onClick={() => setActiveTab('students')}
                    >
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0">
                                <AlertTriangle className="h-6 w-6 text-red-600" />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-bold text-theme-primary mb-1">At-Risk Students</h3>
                                <p className="text-sm text-theme-secondary mb-3">
                                    {needsAttention.length} students need your attention
                                </p>
                                <button className="text-sm text-primary-blue font-semibold hover:underline">
                                    View Students →
                                </button>
                            </div>
                        </div>
                    </GlassmorphicCard>
                </div>

                {/* Students Needing Attention */}
                {needsAttention.length > 0 && (
                    <GlassmorphicCard className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-bold text-theme-primary">Students Needing Attention</h3>
                            <button
                                onClick={() => setActiveTab('students')}
                                className="text-sm text-primary-blue font-semibold hover:underline"
                            >
                                View All →
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {needsAttention.slice(0, 4).map((student: any) => (
                                <div
                                    key={student.id}
                                    className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border-l-4 border-l-red-500"
                                >
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-blue to-blue-600 flex items-center justify-center text-white font-bold text-sm">
                                            {student.name.split(' ').map((n: string) => n[0]).join('')}
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-semibold text-theme-primary">{student.name}</p>
                                            <p className="text-xs text-theme-secondary">
                                                {!student.todayHoursSubmitted ? '❌ No submission today' :
                                                    student.currentStreak === 0 ? '⚠️ Streak broken' :
                                                        '📉 Declining performance'}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleViewStudent(student.id)}
                                        className="w-full py-1.5 bg-primary-blue text-white text-xs font-semibold rounded-lg hover:bg-blue-600 transition-colors"
                                    >
                                        View Profile
                                    </button>
                                </div>
                            ))}
                        </div>
                    </GlassmorphicCard>
                )}

                {/* Recent Activity / Analytics Preview */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <GlassmorphicCard className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-bold text-theme-primary">Batch Performance</h3>
                            <BarChart3 className="h-5 w-5 text-theme-secondary" />
                        </div>
                        <div className="space-y-3">
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm text-theme-secondary">NEET 2025 Batch A</span>
                                    <span className="text-sm font-semibold text-theme-primary">75%</span>
                                </div>
                                <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full" style={{ width: '75%' }} />
                                </div>
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm text-theme-secondary">NEET 2025 Batch B</span>
                                    <span className="text-sm font-semibold text-theme-primary">68%</span>
                                </div>
                                <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                    <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full" style={{ width: '68%' }} />
                                </div>
                            </div>
                        </div>
                    </GlassmorphicCard>

                    <GlassmorphicCard className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-bold text-theme-primary">Unread Messages</h3>
                            <MessageSquare className="h-5 w-5 text-theme-secondary" />
                        </div>
                        <div className="space-y-2">
                            <div className="p-3 bg-blue-50 dark:bg-blue-900/10 rounded-lg">
                                <p className="text-sm font-semibold text-theme-primary">Arun Kumar</p>
                                <p className="text-xs text-theme-secondary mt-1">
                                    Need help with Thermodynamics concept...
                                </p>
                            </div>
                            <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                <p className="text-sm font-semibold text-theme-primary">Priya Sharma</p>
                                <p className="text-xs text-theme-secondary mt-1">
                                    Thank you for the feedback!
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setActiveTab('chat')}
                            className="w-full mt-3 py-2 bg-primary-blue text-white text-sm font-semibold rounded-lg hover:bg-blue-600 transition-colors"
                        >
                            View All Messages
                        </button>
                    </GlassmorphicCard>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
            <div className="flex h-screen overflow-hidden">
                {/* Sidebar - Desktop */}
                <div className="hidden lg:block shrink-0">
                    <MentorSidebar activeTab={activeTab} setActiveTab={setActiveTab} />
                </div>

                {/* Mobile Menu */}
                <MentorMobileMenu
                    isOpen={isMobileMenuOpen}
                    onClose={() => setIsMobileMenuOpen(false)}
                    activeTab={activeTab}
                    onNavigate={handleMobileMenuNavigate}
                />

                {/* Main Content */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Header */}
                    <div className="p-4 md:p-6 border-b border-slate-200 dark:border-slate-800 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm sticky top-0 z-30">
                        <MentorHeader onMenuClick={() => setIsMobileMenuOpen(true)} />
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 overflow-y-auto p-4 md:p-6">
                        {renderContent()}
                    </div>
                </div>
            </div>

            {/* Bottom Navigation - Mobile */}
            <MentorBottomNav activeTab={activeTab} setActiveTab={setActiveTab} />
        </div>
    )
}
